import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ShieldCheck, Terminal, Cpu, Activity, Database, Server, Code, Lock, Network, Key, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import { useTranslation } from './I18nContext';

const PARTNERS = [
  "Nvidia", 
  "Proxmox", 
  "Ubuntu", 
  "Mistral AI", 
  "Black Forest Labs", 
  "Home Assistant", 
  "Nextcloud", 
  "TensorFlow"
];

// Animation variants for floating center elements
const floatAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      ease: "easeInOut" as const,
      repeat: Infinity,
    },
  },
};

const floatAnimationDelayed = {
  initial: { y: 0 },
  animate: {
    y: [10, -10, 10],
    transition: {
      duration: 7,
      ease: "easeInOut" as const,
      repeat: Infinity,
      delay: 1
    } 
  }
};

// Simple Card Component for Edge Graphics
const EdgeCard = ({ children, className = "" }: { children?: React.ReactNode; className?: string }) => (
  <div className={`p-4 bg-white/80 dark:bg-[#1e293b]/80 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-white/10 shadow-xl ${className}`}>
    {children}
  </div>
);

export const Hero: React.FC = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const { t } = useTranslation();
  
  // Transform values for scroll-away effect
  const leftX = useTransform(scrollY, [0, 500], [0, -400]);
  const rightX = useTransform(scrollY, [0, 500], [0, 400]);
  const topY = useTransform(scrollY, [0, 500], [0, -300]);
  const bottomY = useTransform(scrollY, [0, 500], [0, 300]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen w-full flex flex-col justify-center items-center bg-slate-50 dark:bg-[#020617] overflow-hidden pt-20 lg:pt-0 transition-colors duration-300">
      
      {/* Background Gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-[#0ea5e9]/5 dark:bg-[#0ea5e9]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-indigo-500/10 dark:bg-indigo-900/20 rounded-full blur-[100px]" />
        <div 
          className="absolute inset-0 opacity-[0.1]" 
          style={{
            backgroundImage: `radial-gradient(#64748b 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        />
      </div>

      {/* --- EXTRA GRAPHICS (Top/Bottom scattered) --- */}
      
      {/* Top Left - API Key */}
      <motion.div style={{ x: leftX, y: topY, opacity }} className="absolute top-24 left-[10%] hidden xl:block z-0">
         <EdgeCard className="rotate-[-12deg] w-40">
            <div className="flex items-center gap-2 mb-2">
               <div className="p-1.5 bg-yellow-100 dark:bg-yellow-500/20 rounded text-yellow-600 dark:text-yellow-400">
                  <Key className="w-4 h-4" />
               </div>
               <span className="text-[10px] font-bold text-slate-500 uppercase">API Key</span>
            </div>
            <div className="text-xs font-mono bg-slate-100 dark:bg-black/20 p-2 rounded text-slate-600 dark:text-slate-300">
               sk_live_51...
            </div>
         </EdgeCard>
      </motion.div>

      {/* Top Right - Network */}
      <motion.div style={{ x: rightX, y: topY, opacity }} className="absolute top-32 right-[8%] hidden xl:block z-0">
         <EdgeCard className="rotate-[8deg] w-44">
            <div className="flex justify-between items-center mb-1">
               <span className="text-xs font-bold text-slate-700 dark:text-white">Network</span>
               <Network className="w-4 h-4 text-[#0ea5e9]" />
            </div>
            <div className="text-xl font-bold text-slate-900 dark:text-white">10 Gbit/s</div>
            <div className="w-full bg-slate-100 dark:bg-slate-700 h-1 rounded-full mt-2">
               <div className="bg-[#0ea5e9] h-1 rounded-full w-[85%]"></div>
            </div>
         </EdgeCard>
      </motion.div>

      {/* Bottom Left - Backup */}
      <motion.div style={{ x: leftX, y: bottomY, opacity }} className="absolute bottom-32 left-[5%] hidden xl:block z-0">
         <EdgeCard className="rotate-[15deg] w-36">
            <div className="flex flex-col items-center">
               <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center mb-2 text-purple-600 dark:text-purple-400">
                  <Database className="w-4 h-4" />
               </div>
               <span className="text-xs font-bold text-slate-700 dark:text-white">Backup</span>
               <span className="text-[10px] text-slate-400">Last: 2m ago</span>
            </div>
         </EdgeCard>
      </motion.div>
      
      {/* Bottom Right - Users */}
      <motion.div style={{ x: rightX, y: bottomY, opacity }} className="absolute bottom-40 right-[15%] hidden xl:block z-0">
         <EdgeCard className="rotate-[-5deg] w-48">
            <div className="flex items-center gap-3">
               <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white dark:border-slate-800"></div>
                  <div className="w-8 h-8 rounded-full bg-slate-300 border-2 border-white dark:border-slate-800"></div>
                  <div className="w-8 h-8 rounded-full bg-slate-400 border-2 border-white dark:border-slate-800 flex items-center justify-center text-[10px] text-white font-bold">+4</div>
               </div>
               <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">Team Access</div>
                  <div className="text-[10px] text-green-500">Active now</div>
               </div>
            </div>
         </EdgeCard>
      </motion.div>


      {/* --- EXISTING SIDE GRAPHICS --- */}
      <div className="absolute left-0 top-0 bottom-0 w-[15%] hidden xl:flex flex-col justify-center gap-12 pointer-events-none z-0 px-4">
        <motion.div style={{ x: leftX, opacity }} className="self-end translate-x-12">
           <EdgeCard className="rotate-[-6deg] w-48">
              <div className="flex items-center gap-3 mb-2">
                 <div className="p-2 bg-orange-100 dark:bg-orange-500/20 rounded-lg text-orange-600 dark:text-orange-400">
                    <Code className="w-5 h-5" />
                 </div>
                 <span className="text-xs font-mono font-bold text-slate-600 dark:text-slate-300">main.tsx</span>
              </div>
              <div className="space-y-1">
                 <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                 <div className="h-1.5 w-2/3 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                 <div className="h-1.5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
              </div>
           </EdgeCard>
        </motion.div>
        
        <motion.div style={{ x: leftX, opacity }} className="self-start">
           <EdgeCard className="rotate-[3deg] w-40">
              <div className="flex flex-col items-center text-center">
                 <div className="w-12 h-12 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center mb-2 text-blue-600 dark:text-blue-400">
                    <Database className="w-6 h-6" />
                 </div>
                 <span className="text-xs font-bold text-slate-700 dark:text-white">PostgreSQL</span>
                 <span className="text-[10px] text-green-500">● Connected</span>
              </div>
           </EdgeCard>
        </motion.div>
      </div>

      <div className="absolute right-0 top-0 bottom-0 w-[15%] hidden xl:flex flex-col justify-center gap-24 pointer-events-none z-0 px-4">
         <motion.div style={{ x: rightX, opacity }} className="self-start -translate-x-4">
           <EdgeCard className="rotate-[6deg] w-48">
              <div className="flex items-center justify-between mb-3">
                 <span className="text-xs font-bold text-slate-700 dark:text-white">CPU Usage</span>
                 <Activity className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="flex items-end gap-1 h-12 justify-between px-1">
                 <div className="w-3 bg-emerald-500/20 h-full rounded-sm relative"><div className="absolute bottom-0 w-full bg-emerald-500 rounded-sm h-[40%]"></div></div>
                 <div className="w-3 bg-emerald-500/20 h-full rounded-sm relative"><div className="absolute bottom-0 w-full bg-emerald-500 rounded-sm h-[60%]"></div></div>
                 <div className="w-3 bg-emerald-500/20 h-full rounded-sm relative"><div className="absolute bottom-0 w-full bg-emerald-500 rounded-sm h-[30%]"></div></div>
                 <div className="w-3 bg-emerald-500/20 h-full rounded-sm relative"><div className="absolute bottom-0 w-full bg-emerald-500 rounded-sm h-[75%]"></div></div>
                 <div className="w-3 bg-emerald-500/20 h-full rounded-sm relative"><div className="absolute bottom-0 w-full bg-emerald-500 rounded-sm h-[50%]"></div></div>
              </div>
           </EdgeCard>
        </motion.div>

        <motion.div style={{ x: rightX, opacity }} className="self-end -translate-x-12">
           <EdgeCard className="rotate-[-3deg] w-44">
              <div className="flex items-center gap-3">
                 <div className="p-2 bg-indigo-100 dark:bg-indigo-500/20 rounded-lg text-indigo-600 dark:text-indigo-400">
                    <Server className="w-5 h-5" />
                 </div>
                 <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Node Status</div>
                    <div className="text-sm font-bold text-slate-800 dark:text-white">Active</div>
                 </div>
              </div>
           </EdgeCard>
        </motion.div>
      </div>


      {/* Main Hero Content */}
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative z-10 w-full flex-grow">
        
        {/* Left Column: Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="text-left"
        >
          {/* Brand/Tag */}
          <div className="flex items-center gap-2 mb-8">
             <span className="font-bold text-2xl tracking-tight text-slate-900 dark:text-white italic">
               Hensel<span className="text-[#0ea5e9]">X</span>
             </span>
             <span className="text-[10px] font-mono text-[#0ea5e9] bg-[#0ea5e9]/10 px-2 py-0.5 rounded border border-[#0ea5e9]/20">
               {t('hero.tag')}
             </span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-[1.1] mb-6 whitespace-pre-line">
            {t('hero.title')}
            <span className="text-[#0ea5e9]">{t('hero.titleHighlight')}</span>
          </h1>

          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-lg leading-relaxed">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-12">
            <Link to="/contact">
              <Button className="h-14 px-8 text-base shadow-xl shadow-[#0ea5e9]/20 rounded-full">
                {t('hero.cta')}
              </Button>
            </Link>
            <Link to="/services/infrastructure">
               <button className="h-14 px-8 rounded-full border border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-200 dark:hover:bg-white/5 transition-colors flex items-center gap-2">
                 {t('hero.solutions')} <ArrowRight className="w-4 h-4" />
               </button>
            </Link>
          </div>

          {/* "App Store" style badges replacement */}
          <div className="flex items-center gap-6">
             <div className="flex items-center gap-3 px-4 py-2 bg-white/50 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/5 hover:border-[#0ea5e9]/30 transition-colors cursor-default">
                <ShieldCheck className="w-8 h-8 text-slate-900 dark:text-white" />
                <div className="flex flex-col">
                   <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('hero.badge_security')}</span>
                   <span className="text-sm font-bold text-slate-900 dark:text-white">{t('hero.badge_gdpr')}</span>
                </div>
             </div>
             <div className="flex items-center gap-3 px-4 py-2 bg-white/50 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/5 hover:border-[#0ea5e9]/30 transition-colors cursor-default">
                <Database className="w-8 h-8 text-slate-900 dark:text-white" />
                <div className="flex flex-col">
                   <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('hero.badge_hosted')}</span>
                   <span className="text-sm font-bold text-slate-900 dark:text-white">{t('hero.badge_location')}</span>
                </div>
             </div>
          </div>
        </motion.div>

        {/* Right Column: 3D Visual Composition */}
        <div className="relative h-[600px] w-full hidden lg:block perspective-1000">
          
          {/* Decorative Squiggles */}
          <svg className="absolute top-0 right-10 w-24 h-24 text-slate-400 dark:text-slate-700 opacity-50 z-0" viewBox="0 0 100 100">
             <path d="M10,50 Q25,25 50,50 T90,50" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          </svg>
          <svg className="absolute bottom-10 left-10 w-16 h-16 text-[#0ea5e9] opacity-30 z-0 rotate-45" viewBox="0 0 100 100">
             <rect x="20" y="20" width="60" height="60" fill="none" stroke="currentColor" strokeWidth="8" rx="10" />
          </svg>

          {/* BACK CARD: Server Stats */}
          <motion.div 
            variants={floatAnimationDelayed}
            initial="initial"
            animate="animate"
            className="absolute top-10 right-0 w-80 h-96 bg-white dark:bg-[#0f172a] rounded-3xl border border-slate-200 dark:border-white/10 shadow-2xl p-6 rotate-12 z-10 backdrop-blur-xl bg-opacity-90 dark:bg-opacity-80"
          >
             <div className="flex items-center justify-between mb-6">
                <div className="flex gap-2">
                   <div className="w-3 h-3 rounded-full bg-red-500/20"></div>
                   <div className="w-3 h-3 rounded-full bg-yellow-500/20"></div>
                   <div className="w-3 h-3 rounded-full bg-green-500/20"></div>
                </div>
                <span className="text-xs font-mono text-slate-400 dark:text-slate-500">server_mon.sh</span>
             </div>
             <div className="space-y-4">
                <div className="space-y-2">
                   <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>CPU Load</span>
                      <span>12%</span>
                   </div>
                   <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full w-[12%] bg-emerald-500 rounded-full"></div>
                   </div>
                </div>
                <div className="space-y-2">
                   <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>Memory (RAM)</span>
                      <span>64GB / 128GB</span>
                   </div>
                   <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full w-[50%] bg-[#0ea5e9] rounded-full"></div>
                   </div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-black/40 rounded-xl mt-4">
                   <div className="flex items-center gap-3 mb-2">
                      <Activity className="w-4 h-4 text-green-500 dark:text-green-400" />
                      <span className="text-sm text-slate-900 dark:text-white font-medium">System Stable</span>
                   </div>
                   <p className="text-xs text-slate-500">Uptime: 412 days</p>
                </div>
                {/* Map Graphic */}
                <div className="h-24 rounded-xl bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 relative overflow-hidden flex items-center justify-center">
                   <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(#0ea5e9 1px, transparent 1px)', backgroundSize: '8px 8px'}}></div>
                   <div className="w-3 h-3 bg-[#0ea5e9] rounded-full animate-ping"></div>
                   <span className="text-[10px] font-bold text-[#0ea5e9] mt-8">Acri, IT</span>
                </div>
             </div>
          </motion.div>

          {/* FRONT CARD: AI Chat */}
          <motion.div 
            variants={floatAnimation}
            initial="initial"
            animate="animate"
            className="absolute top-20 left-10 w-80 bg-white dark:bg-[#1e293b] rounded-3xl border border-slate-200 dark:border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 -rotate-6 z-20 backdrop-blur-xl"
          >
             <div className="flex items-center gap-4 mb-6 border-b border-slate-100 dark:border-white/5 pb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-[#0ea5e9] to-indigo-500 flex items-center justify-center shadow-lg">
                   <Cpu className="w-5 h-5 text-white" />
                </div>
                <div>
                   <h3 className="font-bold text-slate-900 dark:text-white text-sm">HenselX AI</h3>
                   <p className="text-xs text-green-500 dark:text-green-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 dark:bg-green-400"></span> Local Model
                   </p>
                </div>
             </div>

             <div className="space-y-4">
                {/* Chat Bubble 1 */}
                <div className="flex gap-3">
                   <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex-shrink-0"></div>
                   <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-2xl rounded-tl-none text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                      Analyze system security logs for the last 24h.
                   </div>
                </div>
                {/* Chat Bubble 2 */}
                <div className="flex gap-3 flex-row-reverse">
                   <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#0ea5e9] to-indigo-500 flex-shrink-0 flex items-center justify-center text-[10px] text-white font-bold">AI</div>
                   <div className="bg-[#0ea5e9] p-3 rounded-2xl rounded-tr-none text-xs text-white leading-relaxed shadow-lg shadow-[#0ea5e9]/20">
                      Scanning logs... 0 threats detected. All endpoints secure.
                   </div>
                </div>
                
                {/* Input Area */}
                <div className="mt-4 pt-2">
                   <div className="h-10 bg-slate-50 dark:bg-black/20 rounded-full border border-slate-200 dark:border-white/5 flex items-center px-4 justify-between">
                      <div className="w-20 h-2 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                      <div className="w-6 h-6 rounded-full bg-[#0ea5e9] flex items-center justify-center">
                         <ArrowRight className="w-3 h-3 text-white" />
                      </div>
                   </div>
                </div>
             </div>
          </motion.div>

          {/* FLOATING BADGE: Approved */}
          <motion.div
             animate={{ y: [-15, 5, -15], rotate: [5, -5, 5] }}
             transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
             className="absolute bottom-20 right-20 bg-white dark:bg-white text-[#020617] px-6 py-4 rounded-2xl shadow-2xl z-30 flex items-center gap-4 border border-slate-100 dark:border-transparent"
          >
             <div className="p-2 bg-green-100 rounded-full">
                <ShieldCheck className="w-6 h-6 text-green-600" />
             </div>
             <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Status</p>
                <p className="text-lg font-bold">100% Secure</p>
             </div>
          </motion.div>

          {/* FLOATING ICON: Code */}
          <motion.div
             animate={{ y: [10, -10, 10], x: [5, -5, 5] }}
             transition={{ duration: 4, ease: "easeInOut", repeat: Infinity }}
             className="absolute top-1/2 left-0 -ml-10 bg-[#0ea5e9] p-4 rounded-2xl shadow-lg z-30 transform -rotate-12"
          >
             <Terminal className="w-6 h-6 text-white" />
          </motion.div>

        </div>
      </div>

      {/* --- MARQUEE SECTION (Integrated into Hero Bottom) --- */}
      <div className="absolute bottom-0 w-full pb-6 pt-12 z-20 bg-gradient-to-t from-slate-50 via-slate-50/80 to-transparent dark:from-[#020617] dark:via-[#020617]/80 dark:to-transparent">
        <div className="max-w-7xl mx-auto px-6 mb-4">
          <p className="text-center text-xs uppercase tracking-widest text-slate-500 font-semibold opacity-70">Powered by Open Standards</p>
        </div>
        
        <div 
          className="relative flex overflow-hidden max-w-[70%] mx-auto"
          style={{
            maskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 15%, black 85%, transparent)'
          }}
        >
          <div className="flex animate-[marquee_40s_linear_infinite] gap-16 items-center whitespace-nowrap min-w-full">
            {[...PARTNERS, ...PARTNERS].map((partner, i) => (
              <span key={i} className="text-xl font-bold text-slate-400 dark:text-slate-600 uppercase tracking-tighter hover:text-slate-600 dark:hover:text-slate-400 transition-colors cursor-default">
                {partner}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};